/**
 * OS Rocks TanStack Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import * as api from '../api';
import type { OwnerOption } from '../api';
import type { RockFilters, CreateRockInput, UpdateRockInput } from '../types';

// =============================================================================
// Helpers
// =============================================================================

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'An unexpected error occurred';
}

// =============================================================================
// Query Keys
// =============================================================================

export const rockKeys = {
  all: ['rocks'] as const,
  list: (filters?: RockFilters) => [...rockKeys.all, 'list', filters] as const,
  detail: (id: string) => [...rockKeys.all, 'detail', id] as const,
};

// =============================================================================
// Query Hooks
// =============================================================================

export function useRocks(filters?: RockFilters) {
  return useQuery({
    queryKey: rockKeys.list(filters),
    queryFn: () => api.getRocks(filters),
  });
}

export function useOwnerOptions() {
  return useQuery<OwnerOption[]>({
    queryKey: ['rock-owner-options'],
    queryFn: () => api.fetchOwnerOptions(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useRock(id: string) {
  return useQuery({
    queryKey: rockKeys.detail(id),
    queryFn: () => api.getRock(id),
    enabled: !!id,
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

export function useCreateRock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateRockInput) => api.createRock(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rockKeys.all });
      toast.success('Rock created');
    },
    onError: (e) => toast.error('Failed to create rock', { description: getErrorMessage(e) }),
  });
}

export function useUpdateRock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRockInput }) => api.updateRock(id, data),
    onSuccess: (_result, variables) => {
      qc.invalidateQueries({ queryKey: rockKeys.all });
      qc.invalidateQueries({ queryKey: rockKeys.detail(variables.id) });
      toast.success('Rock updated');
    },
    onError: (e) => toast.error('Failed to update rock', { description: getErrorMessage(e) }),
  });
}

export function useDeleteRock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteRock(id),
    onSuccess: (_result, id) => {
      qc.invalidateQueries({ queryKey: rockKeys.all });
      qc.invalidateQueries({ queryKey: rockKeys.detail(id) });
      toast.success('Rock deleted');
    },
    onError: (e) => toast.error('Failed to delete rock', { description: getErrorMessage(e) }),
  });
}

export function useUpdateRockStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.updateRockStatus(id, status),
    onSuccess: (_result, variables) => {
      qc.invalidateQueries({ queryKey: rockKeys.all });
      qc.invalidateQueries({ queryKey: rockKeys.detail(variables.id) });
      toast.success('Rock status updated');
    },
    onError: (e) =>
      toast.error('Failed to update rock status', { description: getErrorMessage(e) }),
  });
}

export function useArchiveRock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.archiveRock(id),
    onSuccess: (_result, id) => {
      qc.invalidateQueries({ queryKey: rockKeys.all });
      qc.invalidateQueries({ queryKey: rockKeys.detail(id) });
      toast.success('Rock archived');
    },
    onError: (e) => toast.error('Failed to archive rock', { description: getErrorMessage(e) }),
  });
}

export function useUnarchiveRock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.unarchiveRock(id),
    onSuccess: (_result, id) => {
      qc.invalidateQueries({ queryKey: rockKeys.all });
      qc.invalidateQueries({ queryKey: rockKeys.detail(id) });
      toast.success('Rock unarchived');
    },
    onError: (e) => toast.error('Failed to unarchive rock', { description: getErrorMessage(e) }),
  });
}

export function useReorderRock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: { sort_order?: number; board_column?: string };
    }) => api.reorderRock(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rockKeys.all });
    },
    onError: (e) => toast.error('Failed to reorder rock', { description: getErrorMessage(e) }),
  });
}
